/**
 * Thin printable slip payloads for SO / PO / GR / delivery / payments / JV / return — plan/09, plan/24.
 */
import { d, money, moneyDisplay } from "@icraftx/shared/v1";

export type DocPrintLine = {
  name: string;
  qty: string;
  unitPrice: string;
  lineTotal: string;
  /** When set, DocSlip renders Dr/Cr instead of qty × price (journal vouchers). */
  debit?: string;
  credit?: string;
};

export type DocPrintData = {
  title: string;
  number: string;
  partyLabel: string;
  partyName: string;
  /** Primary phone / email / address under the party — plan/47. */
  partyLines?: string[];
  statusLine: string;
  lines: DocPrintLine[];
  totals: Array<{ label: string; value: string }>;
  footerNote?: string;
  issuedAt: string;
};

/** Build quiet party contact lines for slips (skip empties). */
export function partyContactLines(input: {
  primaryPhone?: string | null;
  primaryEmail?: string | null;
  primaryAddress?: string | null;
}): string[] {
  const out: string[] = [];
  if (input.primaryPhone?.trim()) out.push(input.primaryPhone.trim());
  if (input.primaryEmail?.trim()) out.push(input.primaryEmail.trim());
  if (input.primaryAddress?.trim()) out.push(input.primaryAddress.trim());
  return out;
}

export function formatDocIssuedAt(iso: string | Date | null | undefined): string {
  if (iso == null) return "";
  const dt = typeof iso === "string" ? new Date(iso) : iso;
  if (Number.isNaN(dt.getTime())) return String(iso);
  return dt.toLocaleString();
}

export function buildSoPrint(input: {
  number: string;
  customerName: string;
  lifecycle: string;
  pay: string;
  deliver: string;
  discountPct?: string;
  netTotal: string;
  taxTotal: string;
  grossTotal: string;
  amountPaid?: string;
  customerRef?: string | null;
  primaryPhone?: string | null;
  primaryEmail?: string | null;
  primaryAddress?: string | null;
  lines: Array<{
    productName: string;
    qtyOrdered: string;
    unitPrice: string;
    lineGross: string;
  }>;
  issuedAt?: string | null;
}): DocPrintData {
  const disc = input.discountPct?.trim() ? d(input.discountPct) : d(0);
  const totals: DocPrintData["totals"] = [];
  if (disc.gt(0)) {
    totals.push({ label: "Discount", value: `${moneyDisplay(disc, 0)}%` });
  }
  totals.push(
    { label: "Net", value: `₱${moneyDisplay(input.netTotal)}` },
    { label: "Tax", value: `₱${moneyDisplay(input.taxTotal)}` },
    { label: "Total", value: `₱${moneyDisplay(input.grossTotal)}` },
  );
  if (input.amountPaid != null) {
    totals.push({
      label: "Paid",
      value: `₱${moneyDisplay(input.amountPaid)}`,
    });
  }
  return {
    title: "Sales order",
    number: input.number,
    partyLabel: "Customer",
    partyName: input.customerName,
    partyLines: partyContactLines(input),
    statusLine: [
      input.lifecycle,
      `pay ${input.pay}`,
      `deliver ${input.deliver}`,
      input.customerRef ? `ref ${input.customerRef}` : null,
    ]
      .filter(Boolean)
      .join(" · "),
    lines: input.lines.map((l) => ({
      name: l.productName,
      qty: l.qtyOrdered,
      unitPrice: l.unitPrice,
      lineTotal: l.lineGross,
    })),
    totals,
    issuedAt: input.issuedAt ?? new Date().toISOString(),
  };
}

export function buildSiPrint(input: {
  number: string;
  customerName: string;
  lifecycle: string;
  docDate: string;
  paymentDueOn?: string | null;
  discountPct?: string;
  netTotal: string;
  taxTotal: string;
  grossTotal: string;
  customerRef?: string | null;
  salesOrderNumber?: string | null;
  primaryPhone?: string | null;
  primaryEmail?: string | null;
  primaryAddress?: string | null;
  lines: Array<{
    productName: string;
    qty: string;
    unitPrice: string;
    lineGross: string;
  }>;
  issuedAt?: string | null;
}): DocPrintData {
  const disc = input.discountPct?.trim() ? d(input.discountPct) : d(0);
  const totals: DocPrintData["totals"] = [];
  if (disc.gt(0)) {
    totals.push({ label: "Discount", value: `${moneyDisplay(disc, 0)}%` });
  }
  totals.push(
    { label: "Net", value: `₱${moneyDisplay(input.netTotal)}` },
    { label: "Tax", value: `₱${moneyDisplay(input.taxTotal)}` },
    { label: "Total", value: `₱${moneyDisplay(input.grossTotal)}` },
  );
  return {
    title: "Sales invoice",
    number: input.number,
    partyLabel: "Customer",
    partyName: input.customerName,
    partyLines: partyContactLines(input),
    statusLine: [
      input.lifecycle,
      `date ${input.docDate}`,
      input.paymentDueOn ? `due ${input.paymentDueOn}` : null,
      input.salesOrderNumber ? `SO ${input.salesOrderNumber}` : null,
      input.customerRef ? `ref ${input.customerRef}` : null,
    ]
      .filter(Boolean)
      .join(" · "),
    lines: input.lines.map((l) => ({
      name: l.productName,
      qty: l.qty,
      unitPrice: l.unitPrice,
      lineTotal: l.lineGross,
    })),
    totals,
    issuedAt: input.issuedAt ?? new Date().toISOString(),
  };
}

export function buildPiPrint(input: {
  number: string;
  supplierName: string;
  lifecycle: string;
  docDate: string;
  paymentDueOn?: string | null;
  discountPct?: string;
  netTotal: string;
  taxTotal: string;
  grossTotal: string;
  supplierInvoiceRef?: string | null;
  supplierRef?: string | null;
  purchaseOrderNumber?: string | null;
  primaryPhone?: string | null;
  primaryEmail?: string | null;
  primaryAddress?: string | null;
  lines: Array<{
    productName: string;
    qty: string;
    unitCost: string;
    lineGross: string;
  }>;
  issuedAt?: string | null;
}): DocPrintData {
  const disc = input.discountPct?.trim() ? d(input.discountPct) : d(0);
  const totals: DocPrintData["totals"] = [];
  if (disc.gt(0)) {
    totals.push({ label: "Discount", value: `${moneyDisplay(disc, 0)}%` });
  }
  totals.push(
    { label: "Net", value: `₱${moneyDisplay(input.netTotal)}` },
    { label: "Tax", value: `₱${moneyDisplay(input.taxTotal)}` },
    { label: "Total", value: `₱${moneyDisplay(input.grossTotal)}` },
  );
  return {
    title: "Purchase invoice",
    number: input.number,
    partyLabel: "Supplier",
    partyName: input.supplierName,
    partyLines: partyContactLines(input),
    statusLine: [
      input.lifecycle,
      `date ${input.docDate}`,
      input.paymentDueOn ? `due ${input.paymentDueOn}` : null,
      input.purchaseOrderNumber ? `PO ${input.purchaseOrderNumber}` : null,
      input.supplierInvoiceRef ? `inv ${input.supplierInvoiceRef}` : null,
      input.supplierRef ? `ref ${input.supplierRef}` : null,
    ]
      .filter(Boolean)
      .join(" · "),
    lines: input.lines.map((l) => ({
      name: l.productName,
      qty: l.qty,
      unitPrice: l.unitCost,
      lineTotal: l.lineGross,
    })),
    totals,
    issuedAt: input.issuedAt ?? new Date().toISOString(),
  };
}

export function buildPoPrint(input: {
  number: string;
  supplierName: string;
  lifecycle: string;
  pay: string;
  receive: string;
  netTotal?: string;
  taxTotal?: string;
  grossTotal: string;
  amountPaid?: string;
  supplierRef?: string | null;
  primaryPhone?: string | null;
  primaryEmail?: string | null;
  primaryAddress?: string | null;
  lines: Array<{
    productName: string;
    qtyOrdered: string;
    unitCost: string;
    lineGross?: string;
  }>;
  issuedAt?: string | null;
}): DocPrintData {
  const totals: DocPrintData["totals"] = [
    { label: "Total", value: `₱${moneyDisplay(input.grossTotal)}` },
  ];
  if (input.amountPaid != null) {
    totals.push({
      label: "Paid",
      value: `₱${moneyDisplay(input.amountPaid)}`,
    });
  }
  return {
    title: "Purchase order",
    number: input.number,
    partyLabel: "Supplier",
    partyName: input.supplierName,
    partyLines: partyContactLines(input),
    statusLine: [
      input.lifecycle,
      `pay ${input.pay}`,
      `receive ${input.receive}`,
      input.supplierRef ? `ref ${input.supplierRef}` : null,
    ]
      .filter(Boolean)
      .join(" · "),
    lines: input.lines.map((l) => {
      const lineTotal =
        l.lineGross ?? money(d(l.qtyOrdered).mul(d(l.unitCost)));
      return {
        name: l.productName,
        qty: l.qtyOrdered,
        unitPrice: l.unitCost,
        lineTotal,
      };
    }),
    totals,
    issuedAt: input.issuedAt ?? new Date().toISOString(),
  };
}

export function buildGrPrint(input: {
  number: string;
  poNumber: string;
  lifecycle: string;
  warehouseName?: string;
  warehouseCode?: string;
  supplierInvoiceRef?: string | null;
  lines: Array<{
    productName: string;
    qty: string;
    unitCost: string;
  }>;
  issuedAt?: string | null;
}): DocPrintData {
  let received = d(0);
  const lines = input.lines.map((l) => {
    const lineTotal = d(l.qty).mul(d(l.unitCost));
    received = received.add(lineTotal);
    return {
      name: l.productName,
      qty: l.qty,
      unitPrice: l.unitCost,
      lineTotal: money(lineTotal),
    };
  });
  return {
    title: "Goods receipt",
    number: input.number,
    partyLabel: "Purchase order",
    partyName: input.poNumber,
    statusLine: [
      input.lifecycle,
      input.warehouseCode || input.warehouseName || null,
      input.supplierInvoiceRef
        ? `invoice ${input.supplierInvoiceRef}`
        : null,
    ]
      .filter(Boolean)
      .join(" · "),
    lines,
    totals: [
      { label: "Received", value: `₱${moneyDisplay(received)}` },
    ],
    footerNote: "Goods receipt",
    issuedAt: input.issuedAt ?? new Date().toISOString(),
  };
}

export function buildSalesDeliveryPrint(input: {
  number: string;
  soNumber: string;
  lifecycle: string;
  warehouseName?: string;
  warehouseCode?: string;
  lines: Array<{
    productName: string;
    qty: string;
    unitCost: string;
  }>;
  issuedAt?: string | null;
}): DocPrintData {
  let issued = d(0);
  const lines = input.lines.map((l) => {
    const lineTotal = d(l.qty).mul(d(l.unitCost));
    issued = issued.add(lineTotal);
    return {
      name: l.productName,
      qty: l.qty,
      unitPrice: l.unitCost,
      lineTotal: money(lineTotal),
    };
  });
  return {
    title: "Sales delivery",
    number: input.number,
    partyLabel: "Sales order",
    partyName: input.soNumber,
    statusLine: [
      input.lifecycle,
      input.warehouseCode || input.warehouseName || null,
    ]
      .filter(Boolean)
      .join(" · "),
    lines,
    totals: [{ label: "Issued", value: `₱${moneyDisplay(issued)}` }],
    footerNote: "Sales delivery",
    issuedAt: input.issuedAt ?? new Date().toISOString(),
  };
}

export function buildJournalPrint(input: {
  number: string;
  lifecycle: string;
  kind: string;
  memo?: string | null;
  sourceDocType?: string | null;
  totalDebit: string;
  totalCredit: string;
  lines: Array<{
    accountCode: string;
    accountName: string;
    debit: string;
    credit: string;
    memo?: string | null;
  }>;
  issuedAt?: string | null;
}): DocPrintData {
  return {
    title: "Journal voucher",
    number: input.number,
    partyLabel: "Kind",
    partyName: input.kind,
    statusLine: [
      input.lifecycle,
      input.memo || null,
      input.sourceDocType || null,
    ]
      .filter(Boolean)
      .join(" · "),
    lines: input.lines.map((l) => {
      const label = `${l.accountCode} ${l.accountName}`.trim();
      const name = l.memo ? `${label} · ${l.memo}` : label;
      const amt = Number(l.debit) ? l.debit : l.credit;
      return {
        name,
        qty: "1",
        unitPrice: amt,
        lineTotal: amt,
        debit: Number(l.debit) ? l.debit : undefined,
        credit: Number(l.credit) ? l.credit : undefined,
      };
    }),
    totals: [
      { label: "Debit", value: `₱${moneyDisplay(input.totalDebit)}` },
      { label: "Credit", value: `₱${moneyDisplay(input.totalCredit)}` },
      { label: "Total", value: `₱${moneyDisplay(input.totalDebit)}` },
    ],
    footerNote: "Journal voucher",
    issuedAt: input.issuedAt ?? new Date().toISOString(),
  };
}

export function buildSupplierPaymentPrint(input: {
  number: string;
  supplierName: string;
  lifecycle: string;
  direction: string;
  amount: string;
  payMethodName?: string | null;
  payMethodCode?: string | null;
  bankRef?: string | null;
  allocations: Array<{ poNumber: string; amount: string }>;
  issuedAt?: string | null;
}): DocPrintData {
  const directionLabel =
    input.direction === "from_supplier" ? "refund / credit" : "to supplier";
  return {
    title: "Supplier payment",
    number: input.number,
    partyLabel: "Supplier",
    partyName: input.supplierName,
    statusLine: [
      input.lifecycle,
      directionLabel,
      input.payMethodName || input.payMethodCode || null,
      input.bankRef ? `ref ${input.bankRef}` : null,
    ]
      .filter(Boolean)
      .join(" · "),
    lines: input.allocations.map((a) => ({
      name: a.poNumber,
      qty: "1",
      unitPrice: a.amount,
      lineTotal: a.amount,
    })),
    totals: [
      { label: "Total", value: `₱${moneyDisplay(input.amount)}` },
    ],
    footerNote: "Supplier payment",
    issuedAt: input.issuedAt ?? new Date().toISOString(),
  };
}

export function buildCustomerPaymentPrint(input: {
  number: string;
  customerName: string;
  lifecycle: string;
  direction: string;
  amount: string;
  payMethodName?: string | null;
  payMethodCode?: string | null;
  bankRef?: string | null;
  allocations: Array<{ soNumber: string; amount: string }>;
  issuedAt?: string | null;
}): DocPrintData {
  const directionLabel =
    input.direction === "to_customer" ? "refund / credit" : "from customer";
  return {
    title: "Customer payment",
    number: input.number,
    partyLabel: "Customer",
    partyName: input.customerName,
    statusLine: [
      input.lifecycle,
      directionLabel,
      input.payMethodName || input.payMethodCode || null,
      input.bankRef ? `ref ${input.bankRef}` : null,
    ]
      .filter(Boolean)
      .join(" · "),
    lines: input.allocations.map((a) => ({
      name: a.soNumber,
      qty: "1",
      unitPrice: a.amount,
      lineTotal: a.amount,
    })),
    totals: [
      { label: "Total", value: `₱${moneyDisplay(input.amount)}` },
    ],
    footerNote: "Customer payment",
    issuedAt: input.issuedAt ?? new Date().toISOString(),
  };
}

export function buildReturnPrint(input: {
  number: string;
  sourceType: string;
  sourceNumber?: string | null;
  lifecycle: string;
  settleMode: string;
  restock: boolean;
  netTotal: string;
  taxTotal: string;
  grossTotal: string;
  lines: Array<{
    description: string;
    qty: string;
    unitPrice: string;
    lineGross: string;
  }>;
  issuedAt?: string | null;
}): DocPrintData {
  return {
    title: "Return",
    number: input.number,
    partyLabel: "Source",
    partyName: input.sourceNumber
      ? `${input.sourceType} ${input.sourceNumber}`
      : input.sourceType,
    statusLine: [
      input.lifecycle,
      input.settleMode,
      input.restock ? "restock" : "no restock",
    ].join(" · "),
    lines: input.lines.map((l) => ({
      name: l.description,
      qty: l.qty,
      unitPrice: l.unitPrice,
      lineTotal: l.lineGross,
    })),
    totals: [
      { label: "Net", value: `₱${moneyDisplay(input.netTotal)}` },
      { label: "Tax", value: `₱${moneyDisplay(input.taxTotal)}` },
      { label: "Total", value: `₱${moneyDisplay(input.grossTotal)}` },
    ],
    footerNote: "Customer return",
    issuedAt: input.issuedAt ?? new Date().toISOString(),
  };
}
